
import React from 'react';

import { Avatar } from '../../../base/avatar';
import { getPictureInPictureModeConfig } from '../../../base/config/functions.any';
import { truncateText } from '../../../base/environment/utils';
import { translate } from '../../../base/i18n';
import { isLocalParticipantModerator } from '../../../base/participants';
import { connect } from '../../../base/redux';
import { getBrandingColorPalette } from '../../../base/styles';
import { LobbySection } from '../../../lobby';
import { showNotification } from '../../../notifications';
import { isToolboxVisible } from '../../../toolbox/functions.web';
import { admitMultiple, approveRequest, denyRequest } from '../../../visitors/actions';
import { getPromotionRequests } from '../../../visitors/functions';
import type { IPromotionRequest } from '../../../visitors/types';
import { showLobbyAdmissionDialog } from '../../actions.web';
import AbstractKnockingParticipantList, {
    type Props as AbstractProps,
    mapStateToProps as abstractMapStateToProps
} from '../AbstractKnockingParticipantList';


declare var interfaceConfig: Object;
declare var config: object;

type Props = AbstractProps & {

    /**
     * Visitors asking to be promoted into the main conference.
     */
    _promotionRequests: Array<IPromotionRequest>,

    /**
     * True if the toolbox is visible, so we need to adjust the position.
     */
    _toolboxVisible: boolean,
};

/**
 * Component to render a list for the actively knocking participants.
 */
class KnockingParticipantList extends AbstractKnockingParticipantList<Props> {
    /**
     * Admits every lobby guest and visitor promotion request that fits in the meeting.
     */
    _onAdmitAllParticipants = () => {
        const {
            _knockingParticipants,
            _participantsinConference,
            _promotionRequests,
            participantAllowedinMeeting,
            dispatch
        } = this.props;
        const participantLimit = Number(participantAllowedinMeeting);
        const hasParticipantLimit = participantAllowedinMeeting !== undefined
            && participantAllowedinMeeting !== null
            && Number.isFinite(participantLimit);
        const availableSlots = hasParticipantLimit
            ? Math.max(participantLimit - _participantsinConference.length, 0)
            : Infinity;
        const lobbyGuestsToAdmit = _knockingParticipants.slice(0, availableSlots);
        const visitorSlots = availableSlots - lobbyGuestsToAdmit.length;
        const visitorsToAdmit = _promotionRequests.slice(0, visitorSlots);
        const waitingParticipantsCount = _knockingParticipants.length + _promotionRequests.length;

        const finishAdmission = () => {
            if (visitorsToAdmit.length > 0) {
                dispatch(admitMultiple(visitorsToAdmit));
            }

            if (hasParticipantLimit && waitingParticipantsCount > availableSlots) {
                dispatch(showNotification({
                    descriptionArguments: { number: participantLimit },
                    descriptionKey: 'notify.participantLimitReachedDes',
                    titleKey: 'notify.participantLimitReached',
                    customActionNameKey: 'notify.upgradeNow',
                    customActionHandler: () => window.open(`${config?.URL_PREFIX}updateSubscription`, '_new')
                }));
            }
        };

        if (lobbyGuestsToAdmit.length > 0) {
            dispatch(showLobbyAdmissionDialog(lobbyGuestsToAdmit, finishAdmission));
        } else {
            finishAdmission();
        }
    };

    /**
     * Constructs a response handler for a visitor promotion request.
     *
     * @param {IPromotionRequest} request - The visitor request.
     * @param {boolean} approve - Whether to admit the visitor.
     * @returns {Function}
     */
    _onRespondToVisitor = (request: IPromotionRequest, approve: boolean) => () => {
        this.props.dispatch(approve ? approveRequest(request) : denyRequest(request));
    };

    /**
     * Implements {@code PureComponent#render}.
     *
     * @inheritdoc
     */
    render() {
        const {
            _knockingParticipants,
            _promotionRequests,
            _toolboxVisible,
            _visible,
            _isPictureInPictureVisible,
            t
        } = this.props;
        const smallScreen = window.innerWidth < 500 || _isPictureInPictureVisible;
        const knockingParticipantStyle = {
            backgroundColor: getBrandingColorPalette(interfaceConfig)?.primary
        };
        const rejectButtonStyle = {
            backgroundColor: getBrandingColorPalette(interfaceConfig)?.accent2
        };

        if (!_visible) {
            return null;
        }

        return (
            <div
                className = { _toolboxVisible ? 'toolbox-visible' : '' }
                id = 'knocking-participant-list'
                style = { knockingParticipantStyle }>
                <span className = 'title'>
                    {t('lobby.knockingParticipantList')}
                    <div className = 'knocking-participant-list-actions'>
                        {!_isPictureInPictureVisible && _knockingParticipants.length > 0
                            && <LobbySection hide = { true } />}
                        {_knockingParticipants.length + _promotionRequests.length > 1 && (
                            <button onClick = { this._onAdmitAllParticipants }>
                                {t('lobby.admitAll')}
                            </button>
                        )}
                    </div>
                </span>
                <ul>
                    {_knockingParticipants.map(p => (
                        <li key = { p.id }>
                            <Avatar
                                displayName = { p.name }
                                size = { 48 }
                                testId = 'knockingParticipant.avatar'
                                url = { p.loadableAvatarUrl } />
                            <div className = 'details'>
                                <span data-testid = 'knockingParticipant.name'>
                                    {smallScreen ? truncateText(p.name, 15) : p.name}
                                </span>
                                <span className = 'request-type'>
                                    {t('lobby.guestRequestLabel')}
                                </span>
                                {p.email && (
                                    <span data-testid = 'knockingParticipant.email'>
                                        {smallScreen ? truncateText(p.email, 15) : p.email}
                                    </span>
                                )}
                            </div>
                            <button
                                className = 'primary'
                                data-testid = 'lobby.allow'
                                onClick = { this._onRespondToParticipant(p.id, true) }
                                type = 'button'>
                                {t('lobby.admit')}
                            </button>
                            <button
                                className = 'borderLess'
                                data-testid = 'lobby.reject'
                                onClick = { this._onRespondToParticipant(p.id, false) }
                                style = { rejectButtonStyle }
                                type = 'button'>
                                {t('lobby.reject')}
                            </button>
                        </li>
                    ))}
                    {_promotionRequests.map(request => (
                        <li key = { `visitor-${request.from}` }>
                            <Avatar
                                displayName = { request.nick }
                                size = { 48 }
                                testId = 'visitorPromotionRequest.avatar' />
                            <div className = 'details'>
                                <span data-testid = 'visitorPromotionRequest.name'>
                                    {smallScreen ? truncateText(request.nick, 15) : request.nick}
                                </span>
                                <span className = 'request-type'>
                                    {t('visitors.promotionRequestLabel')}
                                </span>
                            </div>
                            <button
                                className = 'primary'
                                data-testid = 'visitor.allow'
                                onClick = { this._onRespondToVisitor(request, true) }
                                type = 'button'>
                                {t('lobby.admit')}
                            </button>
                            <button
                                className = 'borderLess'
                                data-testid = 'visitor.reject'
                                onClick = { this._onRespondToVisitor(request, false) }
                                style = { rejectButtonStyle }
                                type = 'button'>
                                {t('lobby.reject')}
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
        );
    }

    _onRespondToParticipant: (string, boolean) => Function;
}

/**
 * Maps part of the Redux state to the props of this component.
 *
 * @param {Object} state - The Redux state.
 * @returns {Props}
 */
function _mapStateToProps(state: Object): $Shape<Props> {
    const abstractProps = abstractMapStateToProps(state);
    const promotionRequests = getPromotionRequests(state);

    return {
        ...abstractProps,
        _promotionRequests: promotionRequests,
        _visible: abstractProps._visible
            || (isLocalParticipantModerator(state) && Boolean(promotionRequests.length)),
        _toolboxVisible: isToolboxVisible(state),
        _isPictureInPictureVisible: getPictureInPictureModeConfig(state)
    };
}

export default translate(connect(_mapStateToProps)(KnockingParticipantList));
